import { Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString, SecondaryButton, TextareaField } from "common";
import React from "react";
import { VariantOption } from "types";
import { object } from "yup";
import { XolaFormik } from "@/components/Common/XolaFormik";

const addValidationSchema = object().shape({
    optionsText: requiredString(),
});

const editValidationSchema = object().shape({
    name: requiredString().max(100, "Option must be 100 characters or less"),
});

interface AddVariantOptionFormValues {
    optionsText: string;
}

interface EditVariantOptionFormValues {
    name: string;
}

interface AddVariantOptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (option: VariantOption | VariantOption[]) => void;
    currentOption?: VariantOption;
    existingOptions?: VariantOption[];
    variantName?: string;
}

export const AddVariantOptionModal = ({
    isOpen,
    onClose,
    onAdd,
    currentOption,
    existingOptions = [],
    variantName,
}: AddVariantOptionModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <AddVariantOptionModalBody
                currentOption={currentOption}
                existingOptions={existingOptions}
                variantName={variantName}
                onClose={onClose}
                onAdd={onAdd}
            />
        </Modal>
    );
};

interface AddVariantOptionModalBodyProps {
    onClose: () => void;
    onAdd: (option: VariantOption | VariantOption[]) => void;
    currentOption?: VariantOption;
    existingOptions: VariantOption[];
    variantName?: string;
}

const AddVariantOptionModalBody = ({
    onClose,
    onAdd,
    currentOption,
    existingOptions,
    variantName,
}: AddVariantOptionModalBodyProps) => {
    const handleSave = (option: VariantOption | VariantOption[]) => {
        onAdd(option);
        onClose();
    };

    if (currentOption) {
        return (
            <EditOptionForm
                option={currentOption}
                existingOptions={existingOptions}
                onClose={onClose}
                onSave={handleSave}
            />
        );
    }

    return (
        <AddOptionsForm
            variantName={variantName}
            existingOptions={existingOptions}
            onClose={onClose}
            onSave={handleSave}
        />
    );
};

interface EditOptionFormProps {
    option: VariantOption;
    existingOptions: VariantOption[];
    onClose: () => void;
    onSave: (option: VariantOption) => void;
}

const EditOptionForm = ({ option, existingOptions, onClose, onSave }: EditOptionFormProps) => {
    const initialValues: EditVariantOptionFormValues = {
        name: option.name,
    };

    const currentOptionNames = new Set(
        existingOptions
            .filter((o) => {
                if (option.id != null) {
                    return o.id !== option.id;
                }
                return o !== option;
            })
            .map((o) => o.name.toLowerCase()),
    );

    const handleSubmit = (values: EditVariantOptionFormValues) => {
        const updated: VariantOption = {
            id: option.id,
            name: values.name.trim(),
        };
        onSave(updated);
    };

    const validate = (values: EditVariantOptionFormValues) => {
        const errors: { name?: string } = {};
        if (currentOptionNames.has(values.name.trim().toLowerCase())) {
            errors.name = "This option already exists";
        }
        return errors;
    };

    return (
        <XolaFormik
            initialValues={initialValues}
            validationSchema={editValidationSchema}
            validateOnChange={false}
            validate={validate}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isSubmitting }) => (
                <>
                    <Modal.Header>Edit Variant Option</Modal.Header>
                    <Modal.Body>
                        <FormField name="name" label="Name">
                            <InputField isRequired />
                        </FormField>
                    </Modal.Body>
                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                            Done
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface AddOptionsFormProps {
    variantName?: string;
    existingOptions: VariantOption[];
    onClose: () => void;
    onSave: (options: VariantOption[]) => void;
}

const AddOptionsForm = ({ variantName, existingOptions, onClose, onSave }: AddOptionsFormProps) => {
    const initialValues: AddVariantOptionFormValues = {
        optionsText: "",
    };

    const currentOptionNames = new Set(existingOptions.map((o) => o.name.toLowerCase()));

    const validateOptions = (values: AddVariantOptionFormValues) => {
        const names = values.optionsText
            .split(/[,\n]/)
            .map((name) => name.trim())
            .filter((name) => name.length > 0);
        const overLimit = names.filter((name) => name.length > 100);
        if (overLimit.length > 0) {
            return { optionsText: "Option names must be less than 100 characters" };
        }
        return {};
    };

    const handleSubmit = (values: AddVariantOptionFormValues) => {
        // Parse comma-separated or newline-separated options
        const optionNames = values.optionsText
            .split(/[,\n]/)
            .map((name) => name.trim())
            .filter((name) => name.length > 0);

        // Filter out duplicates (both from existing and within the input)
        const uniqueNames = new Set<string>();
        const filteredNames = optionNames.filter((name) => {
            const lowerName = name.toLowerCase();
            if (currentOptionNames.has(lowerName) || uniqueNames.has(lowerName)) {
                return false;
            }
            uniqueNames.add(lowerName);
            return true;
        });

        const options: VariantOption[] = filteredNames.map((name) => ({ name }));

        onSave(options);
    };

    return (
        <XolaFormik
            initialValues={initialValues}
            validationSchema={addValidationSchema}
            validateOnChange={false}
            validate={validateOptions}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isSubmitting }) => (
                <>
                    <Modal.Header>Add Variant Options</Modal.Header>
                    <Modal.Body className="space-y-1">
                        {variantName && (
                            <p className="text-center font-normal">
                                Add options for the <strong>{variantName}</strong> variant.
                            </p>
                        )}
                        <FormField
                            name="optionsText"
                            label="Options"
                            tooltip="Input multiple options separated by a comma or a new line."
                        >
                            <TextareaField isRequired rows={5} />
                        </FormField>
                    </Modal.Body>
                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                            Done
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
